import moment, { Moment } from 'moment';

/** How many days the "top results" service runs once activated. */
export const TOP_RESULT_DURATION_DAYS = 3;
/** The first days after publication cannot be picked, the ad already performs best then. */
export const BLOCKED_DAYS_AFTER_PUBLICATION = 3;
/** Wire format of the activation day sent to the gateway (`UpsaleInput.topResultActivationDate`) */
export const ACTIVATION_DATE_FORMAT = 'YYYY-MM-DD';

export type ActivationKind = 'immediate' | 'scheduled';
export type DateInput = Moment | Date | string;

export interface SelectionRules {
  datePosted: DateInput;
  expireDate: DateInput;
  today?: DateInput;
}

/**
 * Calendar day as the gateway sees it. ViewDate strings carry the server offset
 * (e.g. `2026-09-09T00:00:00+02:00`), so the day is read in that offset instead of the
 * browser's timezone, which could shift it by one day. Returned as local midnight so it
 * compares cleanly with DatePicker values.
 */
export const toDay = (date: DateInput): Moment =>
  typeof date === 'string'
    ? moment(moment.parseZone(date).format(ACTIVATION_DATE_FORMAT), ACTIVATION_DATE_FORMAT)
    : moment(date).startOf('day');

/**
 * First day the service may start: after the blocked period, and never before today. Today matters for a
 * live ad (upsale) as well as for a job form restored from an old incomplete job whose start date is stale.
 */
export const getFirstSelectableDate = (
  datePosted: DateInput,
  today: DateInput = moment()
): Moment => {
  const firstAfterBlockedPeriod = toDay(datePosted).add(BLOCKED_DAYS_AFTER_PUBLICATION, 'days');
  const todayStart = toDay(today);
  return firstAfterBlockedPeriod.isAfter(todayStart) ? firstAfterBlockedPeriod : todayStart;
};

/** Last day the service may start: the day before the ad expires. */
export const getLastSelectableDate = (expireDate: DateInput): Moment =>
  toDay(expireDate).subtract(1, 'day');

/** Wire value for the gateway: the chosen day as YYYY-MM-DD, null when the service starts right away. */
export const toActivationDateInput = (date: Moment | null | undefined): string | null =>
  date ? toDay(date).format(ACTIVATION_DATE_FORMAT) : null;

/**
 * A chosen day stops being valid when the publication period moves under it; the form then drops it
 * instead of sending a day the gateway would reject.
 */
export const shouldResetActivationDate = (
  date: Moment | null | undefined,
  rules: SelectionRules
): boolean => !!date && !isDateSelectable(date, rules);

export const isDateSelectable = (date: DateInput, rules: SelectionRules): boolean => {
  const day = toDay(date);
  return (
    !day.isBefore(getFirstSelectableDate(rules.datePosted, rules.today)) &&
    !day.isAfter(getLastSelectableDate(rules.expireDate))
  );
};

export const isInBlockedInitialPeriod = (date: DateInput, datePosted: DateInput): boolean => {
  const day = toDay(date);
  const posted = toDay(datePosted);
  return (
    !day.isBefore(posted) &&
    day.isBefore(posted.clone().add(BLOCKED_DAYS_AFTER_PUBLICATION, 'days'))
  );
};

/** Days the service actually covers, cut short when the ad expires first. */
export const getServiceDays = (activationDate: DateInput, expireDate: DateInput): Moment[] => {
  const start = toDay(activationDate);
  const lastPossibleDay = getLastSelectableDate(expireDate);
  const days: Moment[] = [];
  for (let offset = 0; offset < TOP_RESULT_DURATION_DAYS; offset += 1) {
    const day = start.clone().add(offset, 'days');
    if (day.isAfter(lastPossibleDay)) {
      break;
    }
    days.push(day);
  }
  return days;
};

export const isServiceShortened = (activationDate: DateInput, expireDate: DateInput): boolean =>
  getServiceDays(activationDate, expireDate).length < TOP_RESULT_DURATION_DAYS;

export const getActivationKind = (
  activationDate: DateInput,
  today: DateInput = moment()
): ActivationKind =>
  toDay(activationDate).isSame(toDay(today), 'day') ? 'immediate' : 'scheduled';

/** The "first days are blocked" note only makes sense while those days are still ahead. */
export const shouldShowInitialPeriodHint = (
  datePosted: DateInput,
  today: DateInput = moment()
): boolean => toDay(datePosted).add(BLOCKED_DAYS_AFTER_PUBLICATION, 'days').isAfter(toDay(today));
